import {
    addDays,
    addMonths,
    addQuarters,
    addYears,
    endOfMonth,
    getDay,
    isBefore,
    parseISO,
    setDate,
    setDay,
    setMonth,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subDays,
} from "date-fns";
import { ServiceTemplateRecurrence } from "@common/schemas/serviceTemplateRecurrence.schema";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    MonthRecurrenceType,
    QuarterMonth,
} from "@common/types/serviceTemplateRecurrence";

interface PositionedDateOfMonthOptions {
    date: Date;
    dateOfMonth: number;
}

interface FirstPositionedDateOptions {
    startDate: Date;
    recurrence: ServiceTemplateRecurrence;
    periodStart: Date;
}

interface NextServiceStartOptions {
    current: Date;
    recurrence: ServiceTemplateRecurrence;
}

interface WeeklyServiceStartOptions {
    from: Date;
    weekDays: number[];
    includeFrom: boolean;
}

interface FirstMonthlyServiceStartOptions {
    startDate: Date;
    datesOfMonth: number[];
}

interface NextMonthlyServiceStartOptions {
    current: Date;
    datesOfMonth: number[];
}

// Cap the forward search when locating the first monthly service start so an
// unreachable configuration can't loop forever (e.g. a start date past the
// only selected day-of-month for many consecutive months).
const MAX_MONTHLY_LOOKAHEAD = 24;

function sortedUnique(values: number[]): number[] {
    return [...new Set(values)].sort((a, b) => a - b);
}

// The DB columns are nullable `int[]`, so they can physically hold `[]` (e.g.
// direct writes that bypass Zod validation). `??` only falls back on
// null/undefined, so treat an empty array like a missing value to avoid
// generating zero or far-future occurrences.
function weekDaysOrFallback(recurrence: ServiceTemplateRecurrence): number[] {
    const { weekDays } = recurrence;
    if (weekDays && weekDays.length > 0) {
        return weekDays;
    }
    return [recurrence.weekDay ?? 1];
}

function datesOfMonthOrFallback(
    recurrence: ServiceTemplateRecurrence,
): number[] {
    const { datesOfMonth } = recurrence;
    if (datesOfMonth && datesOfMonth.length > 0) {
        return datesOfMonth;
    }
    return recurrence.dateOfMonth != null ? [recurrence.dateOfMonth] : [1];
}

function positionedDateOfMonth({
    date,
    dateOfMonth,
}: PositionedDateOfMonthOptions): Date {
    return setDate(date, Math.min(dateOfMonth, endOfMonth(date).getDate()));
}

function nextMatchingWeekday({
    from,
    weekDays,
    includeFrom,
}: WeeklyServiceStartOptions): Date {
    const sorted = sortedUnique(weekDays);
    if (sorted.length === 0) {
        return from;
    }
    const startOffset = includeFrom ? 0 : 1;
    const offsets = Array.from(
        { length: 8 - startOffset },
        (_, index) => index + startOffset,
    );
    for (const offset of offsets) {
        const candidate = addDays(from, offset);
        if (sorted.includes(getDay(candidate))) {
            return candidate;
        }
    }
    return from;
}

function nextWeeklyServiceStart({
    from,
    weekDays,
    includeFrom,
}: WeeklyServiceStartOptions): Date {
    if (includeFrom) {
        return nextMatchingWeekday({ from, weekDays, includeFrom: true });
    }

    const sorted = sortedUnique(weekDays);
    if (sorted.length === 0) {
        return from;
    }
    const currentDay = getDay(from);
    const nextInWeek = sorted.find((day) => day > currentDay);
    if (nextInWeek !== undefined) {
        return addDays(from, nextInWeek - currentDay);
    }
    // Wrap to the first selected weekday in the next week.
    const firstDay = sorted[0] ?? 0;
    return addDays(from, 7 - currentDay + firstDay);
}

function firstMonthlyServiceStart({
    startDate,
    datesOfMonth,
}: FirstMonthlyServiceStartOptions): Date {
    const sorted = sortedUnique(datesOfMonth);
    const monthStart = startOfMonth(startDate);
    const monthOffsets = Array.from(
        { length: MAX_MONTHLY_LOOKAHEAD },
        (_, index) => index,
    );
    for (const monthOffset of monthOffsets) {
        const month = addMonths(monthStart, monthOffset);
        for (const day of sorted) {
            const candidate = positionedDateOfMonth({
                date: month,
                dateOfMonth: day,
            });
            if (!isBefore(candidate, startDate)) {
                return candidate;
            }
        }
    }
    return positionedDateOfMonth({
        date: addMonths(monthStart, MAX_MONTHLY_LOOKAHEAD),
        dateOfMonth: sorted[0] ?? 1,
    });
}

function nextMonthlyServiceStart({
    current,
    datesOfMonth,
}: NextMonthlyServiceStartOptions): Date {
    const sorted = sortedUnique(datesOfMonth);
    const monthStart = startOfMonth(current);
    for (const day of sorted) {
        const candidate = positionedDateOfMonth({
            date: monthStart,
            dateOfMonth: day,
        });
        if (candidate.getTime() > current.getTime()) {
            return candidate;
        }
    }
    return positionedDateOfMonth({
        date: addMonths(monthStart, 1),
        dateOfMonth: sorted[0] ?? 1,
    });
}

function positionedDateInMonth(
    date: Date,
    recurrence: ServiceTemplateRecurrence,
): Date {
    if (recurrence.monthRecurrenceType === MonthRecurrenceType.DAY) {
        return setDate(
            date,
            Math.min(recurrence.dateOfMonth ?? 1, endOfMonth(date).getDate()),
        );
    }
    if (
        recurrence.monthRecurrenceType ===
        MonthRecurrenceType.LAST_WEEKDAY_OCCURRENCE
    ) {
        const monthEnd = endOfMonth(date);
        const difference =
            (getDay(monthEnd) - (recurrence.weekDay ?? 1) + 7) % 7;
        return subDays(monthEnd, difference);
    }
    const occurrences = {
        [MonthRecurrenceType.FIRST_WEEKDAY_OCCURRENCE]: 1,
        [MonthRecurrenceType.SECOND_WEEKDAY_OCCURRENCE]: 2,
        [MonthRecurrenceType.THIRD_WEEKDAY_OCCURRENCE]: 3,
        [MonthRecurrenceType.FOURTH_WEEKDAY_OCCURRENCE]: 4,
    };
    if (!recurrence.monthRecurrenceType) {
        return date;
    }
    const monthStart = startOfMonth(date);
    let result = setDay(monthStart, recurrence.weekDay ?? 1, {
        weekStartsOn: 0,
    });
    if (isBefore(result, monthStart)) {
        result = addDays(result, 7);
    }
    return addDays(
        result,
        (occurrences[recurrence.monthRecurrenceType] - 1) * 7,
    );
}

function quarterMonthOffset(quarterMonth: QuarterMonth | null): number {
    if (quarterMonth === QuarterMonth.SECOND) {
        return 1;
    }
    return quarterMonth === QuarterMonth.THIRD ? 2 : 0;
}

function positionPeriodStart(
    periodStart: Date,
    recurrence: ServiceTemplateRecurrence,
): Date {
    if (recurrence.period === ServiceTemplateRecurrencePeriod.QUARTERLY) {
        return positionedDateInMonth(
            addMonths(periodStart, quarterMonthOffset(recurrence.quarterMonth)),
            recurrence,
        );
    }
    if (recurrence.period === ServiceTemplateRecurrencePeriod.ANNUALLY) {
        return positionedDateInMonth(
            setMonth(periodStart, (recurrence.month ?? 1) - 1),
            recurrence,
        );
    }
    return positionedDateInMonth(periodStart, recurrence);
}

function advanceCalendarPeriod(
    date: Date,
    recurrence: ServiceTemplateRecurrence,
): Date {
    switch (recurrence.period) {
        case ServiceTemplateRecurrencePeriod.DAILY:
            return addDays(date, 1);
        case ServiceTemplateRecurrencePeriod.WEEKLY:
            return addDays(date, 7);
        case ServiceTemplateRecurrencePeriod.MONTHLY:
            return addMonths(date, 1);
        case ServiceTemplateRecurrencePeriod.QUARTERLY:
            return addQuarters(date, 1);
        case ServiceTemplateRecurrencePeriod.ANNUALLY:
            return addYears(date, 1);
    }
}

function firstPositionedDate({
    startDate,
    recurrence,
    periodStart,
}: FirstPositionedDateOptions): Date {
    const candidate = positionPeriodStart(periodStart, recurrence);
    return isBefore(candidate, startDate)
        ? positionPeriodStart(
              advanceCalendarPeriod(periodStart, recurrence),
              recurrence,
          )
        : candidate;
}

export function getFirstServiceStart(
    recurrence: ServiceTemplateRecurrence,
): Date {
    const startDate = startOfDay(parseISO(recurrence.startDate));
    switch (recurrence.period) {
        case ServiceTemplateRecurrencePeriod.DAILY:
            return startDate;
        case ServiceTemplateRecurrencePeriod.WEEKLY:
            return nextWeeklyServiceStart({
                from: startDate,
                weekDays: weekDaysOrFallback(recurrence),
                includeFrom: true,
            });
        case ServiceTemplateRecurrencePeriod.MONTHLY:
            return firstMonthlyServiceStart({
                startDate,
                datesOfMonth: datesOfMonthOrFallback(recurrence),
            });
        case ServiceTemplateRecurrencePeriod.QUARTERLY:
            return firstPositionedDate({
                startDate,
                recurrence,
                periodStart: startOfQuarter(startDate),
            });
        case ServiceTemplateRecurrencePeriod.ANNUALLY:
            return firstPositionedDate({
                startDate,
                recurrence,
                periodStart: startOfYear(startDate),
            });
    }
}

export function getNextServiceStart({
    current,
    recurrence,
}: NextServiceStartOptions): Date {
    if (recurrence.period === ServiceTemplateRecurrencePeriod.WEEKLY) {
        return nextWeeklyServiceStart({
            from: current,
            weekDays: weekDaysOrFallback(recurrence),
            includeFrom: false,
        });
    }
    if (recurrence.period === ServiceTemplateRecurrencePeriod.MONTHLY) {
        return nextMonthlyServiceStart({
            current,
            datesOfMonth: datesOfMonthOrFallback(recurrence),
        });
    }
    if (recurrence.period === ServiceTemplateRecurrencePeriod.DAILY) {
        return advanceCalendarPeriod(current, recurrence);
    }
    let periodStart = startOfYear(current);
    if (recurrence.period === ServiceTemplateRecurrencePeriod.QUARTERLY) {
        periodStart = startOfQuarter(current);
    }
    return positionPeriodStart(
        advanceCalendarPeriod(periodStart, recurrence),
        recurrence,
    );
}
